import React, { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import ListItem from "@tiptap/extension-list-item";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import "./tiptap.css"; // Add this CSS (see below)

const MenuBar = ({ editor }) => {
  if (!editor) return null;
  return (
    <div className="flex items-center gap-2 mb-1 text-sm text-gray-600 font-medium">
      <button
        type="button"
        className={`px-1 ${editor.isActive('bold') ? 'font-bold text-black' : ''}`}
        onClick={() => editor.chain().focus().toggleBold().run()}
        tabIndex={-1}
        title="Bold"
      >B</button>
      <span>/</span>
      <button
        type="button"
        className={`px-1 ${editor.isActive('italic') ? 'italic text-black' : ''}`}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        tabIndex={-1}
        title="Italic"
      >I</button>
      <span>/</span>
      <button
        type="button"
        className={`px-1 ${editor.isActive('underline') ? 'underline text-black' : ''}`}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        tabIndex={-1}
        title="Underline"
      >U</button>
      <span className="ml-3">|</span>
      <button
        type="button"
        className={`px-2 py-1 rounded ${editor.isActive('bulletList') ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
        onClick={() => {
          if (editor.isActive('orderedList')) {
            editor.chain().focus().toggleOrderedList().run();
          }
          editor.chain().focus().toggleBulletList().run();
        }}
        tabIndex={-1}
        title="Bullet List"
      >•</button>
      <button
        type="button"
        className={`px-2 py-1 rounded ${editor.isActive('orderedList') ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
        onClick={() => {
          if (editor.isActive('bulletList')) {
            editor.chain().focus().toggleBulletList().run();
          }
          editor.chain().focus().toggleOrderedList().run();
        }}
        tabIndex={-1}
        title="Numbered List"
      >1.</button>
      <span className="ml-3">|</span>
      <button
        type="button"
        className={`px-1 ${editor.isActive({ textAlign: 'left' }) ? 'text-blue-600' : ''}`}
        onClick={() => editor.chain().focus().setTextAlign('left').run()}
        tabIndex={-1}
        title="Align Left"
      >Left</button>
      <button
        type="button"
        className={`px-1 ${editor.isActive({ textAlign: 'center' }) ? 'text-blue-600' : ''}`}
        onClick={() => editor.chain().focus().setTextAlign('center').run()}
        tabIndex={-1}
        title="Align Center"
      >Center</button>
      <button
        type="button"
        className={`px-1 ${editor.isActive({ textAlign: 'right' }) ? 'text-blue-600' : ''}`}
        onClick={() => editor.chain().focus().setTextAlign('right').run()}
        tabIndex={-1}
        title="Align Right"
      >Right</button>
    </div>
  );
};

export default function RichTextEditor({
  value,
  onChange,
  editable = true,
  placeholder = "Type here...",
  minHeight = 120,
}) {
  const editor = useEditor({
    content: value,
    editable,
    extensions: [
      StarterKit.configure({
        // Disable the default list items since we're using custom ones
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      ListItem,
      BulletList.configure({
        HTMLAttributes: {
          class: 'tiptap-bullet-list',
        },
      }),
      OrderedList.configure({
        HTMLAttributes: {
          class: 'tiptap-ordered-list',
        },
      }),
    ],
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    }
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line
  }, [value]);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl shadow-sm p-2">
      {editable && <MenuBar editor={editor} />}
      <div
        className="rounded-lg border border-gray-200 bg-white"
        style={{ minHeight }}
      >
        <EditorContent
          editor={editor}
          className="px-2 py-1 focus:outline-none outline-none"
          spellCheck={true}
          style={{
            minHeight,
            fontSize: "1rem",
            lineHeight: "1.5",
            background: "transparent"
          }}
        />
      </div>
    </div>
  );
}
